import { Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { cn } from "../../../utils/cn.ts"
import {
  CalendarIcon,
  ClockIcon
} from "../../Lead/icons/lead-state-icons.tsx"
import {
  type FollowUpState,
  followUpState,
  formatFollowUp,
  isFollowUpOverdue
} from "../helpers/lead-table-format.ts"

const { Text } = Typography

/** From the client's icon pack: red clock, orange clock, blue clock, purple calendar. */
const FOLLOW_UP_STYLES: Record<
  FollowUpState,
  { icon: typeof ClockIcon; color: string }
> = {
  due: { icon: ClockIcon, color: "#EF4444" },
  upcoming: { icon: ClockIcon, color: "#F59E0B" },
  scheduled: { icon: ClockIcon, color: "#2563EB" },
  rehash: { icon: CalendarIcon, color: "#8B5CF6" }
}

interface LeadFollowUpCellProps {
  date?: string | null
  time?: string | null
  /** Nurturing leads only date the return — rendered as "Rehash: <date>". */
  nurture?: boolean
}

export function LeadFollowUpCell({ date, time, nurture }: LeadFollowUpCellProps) {
  const { t } = useTranslation()
  const { userFormat } = useDateFormat()
  if (!date) return <>{empty}</>

  const isRehash = !!nurture
  const label = formatFollowUp(date, time, { isRehash, userFormat })
  const overdue = isFollowUpOverdue(date, undefined, userFormat)
  const state = followUpState(date, { isRehash, userFormat })

  if (!state) return <>{empty}</>

  const { icon: StateIcon, color } = FOLLOW_UP_STYLES[state]

  return (
    <Flex vertical gap={0}>
      <Flex align={"center"} gap={6}>
        <StateIcon className={"shrink-0 text-sm"} style={{ color }} />
        <Text
          className={cn(overdue && "text-error")}
          ellipsis={{ tooltip: label }}
        >
          {label}
        </Text>
      </Flex>
      {/* Colour is never the only signal, so the word carries it too. */}
      {overdue && (
        <Text type={"danger"} className={"text-xs pl-5"}>
          {t("leadsTable.row.overdue")}
        </Text>
      )}
    </Flex>
  )
}
